import { useEffect, useState } from "react";
import { Check, Loader2, Send, X } from "lucide-react";
import type { VerifiedEvent } from "nostr-tools/pure";
import { JsonBlock } from "@/components/CodeBlock";
import { Field, FieldGroup } from "@/components/HashDisplay";
import { Prop } from "@/components/Prop";
import { formatBytes, toHex } from "@/hashtree/bytes";
import { encodeNhash, encodeNhashPayload } from "@/hashtree/nhash";
import {
  DEFAULT_RELAYS,
  ROOT_KIND,
  buildRootEvent,
  publishToRelays,
  signRootEvent,
  type PublishOutcome,
  type Visibility,
} from "@/hashtree/nostrRoot";
import { CANONICAL_CHUNK_SIZE, CANONICAL_MAX_LINKS } from "@/hashtree/nodes";
import type { HashtreeBuild } from "@/hashtree/useHashtreeBuild";
import { cn } from "@/lib/utils";
import { Slide, SpecNote, Step } from "./Slide";

const VISIBILITY_OPTIONS: { value: Visibility; label: string; detail: string }[] = [
  { value: "public", label: "Public", detail: "The key is published in the event. Anyone who reads it can open the tree." },
  {
    value: "link-private",
    label: "Link-private",
    detail: "The event holds root_key XOR link_key. Only someone with the link key can open it.",
  },
  {
    value: "owner-private",
    label: "Owner-private",
    detail: "The key is NIP-44 encrypted to you. Nobody else can open the tree, including from a link.",
  },
];

export function PublishSlide({
  build,
  onPublished,
  onNext,
}: {
  build: HashtreeBuild;
  onPublished: (event: VerifiedEvent, linkKey: Uint8Array | undefined) => void;
  onNext: () => void;
}) {
  const { tree, treeName, setTreeName, identity, visibility, setVisibility, relays, chunkSize, maxLinks } = build;
  const [event, setEvent] = useState<VerifiedEvent | null>(null);
  const [linkKey, setLinkKey] = useState<Uint8Array | undefined>();
  const [outcomes, setOutcomes] = useState<PublishOutcome[] | null>(null);
  const [publishing, setPublishing] = useState(false);

  // Rebuild the unsigned event whenever anything it commits to changes.
  useEffect(() => {
    if (!tree) return;
    let cancelled = false;
    void buildRootEvent(
      {
        treeName: treeName.trim() || "untitled",
        rootHash: tree.root.encrypted.blobHash,
        rootKey: tree.root.encrypted.chkKey,
        visibility,
      },
      identity.secretKey,
    ).then((built) => {
      if (cancelled) return;
      setEvent(signRootEvent(built.template, identity.secretKey));
      setLinkKey(built.linkKey);
      setOutcomes(null);
    });
    return () => {
      cancelled = true;
    };
  }, [tree, treeName, visibility, identity]);

  if (!tree || !event) return null;

  const nhash = encodeNhash({ rootHash: tree.root.encrypted.blobHash, rootKey: tree.root.encrypted.chkKey });
  const payload = encodeNhashPayload({
    rootHash: tree.root.encrypted.blobHash,
    rootKey: tree.root.encrypted.chkKey,
  });
  const isCanonical = chunkSize === CANONICAL_CHUNK_SIZE && maxLinks === CANONICAL_MAX_LINKS;

  const publish = async () => {
    setPublishing(true);
    const results = await publishToRelays(relays.length ? relays : DEFAULT_RELAYS, event);
    setOutcomes(results);
    setPublishing(false);
    if (results.some((result) => result.ok)) onPublished(event, linkKey);
  };

  const published = outcomes?.some((outcome) => outcome.ok) ?? false;

  return (
    <Slide
      bud={18}
      title="Name the root"
      lede={
        <>
          Your tree exists on the server now, but only you know its root hash. BUD-18 gives it two kinds of name: one
          that pins this exact root forever, and one that can point somewhere new later.
        </>
      }
    >
      <div
        className={cn(
          "rounded-md border px-3 py-2 text-xs",
          isCanonical ? "border-emerald-500/30 bg-emerald-500/10" : "border-amber-500/30 bg-amber-500/10",
        )}
      >
        This root was produced with <code>chunk_size = {formatBytes(chunkSize)}</code> and{" "}
        <code>max_links = {maxLinks}</code>
        {isCanonical
          ? " — the canonical BUD-17 constants."
          : ". It resolves anywhere, but will not dedup against clients using the canonical 2 MiB / 174."}
      </div>

      <Step n={1} title="nhash — the immutable reference">
        <FieldGroup>
          <Field
            label="TLV payload"
            value={toHex(payload)}
            hint="type 0, length 32, root hash — then type 5, length 32, root key"
          />
          <Field label="nhash" value={nhash} hint="The payload, bech32 encoded. Carries the key, so it is a bearer secret." />
        </FieldGroup>
        <SpecNote>
          An <code>nhash</code> always resolves to this exact tree. Publish a new version and the old{" "}
          <code>nhash</code> still returns the old one — which is what makes it right for snapshots and permalinks.
        </SpecNote>
      </Step>

      <Step n={2} title="A kind 30064 event — the mutable reference">
        <div className="space-y-1.5">
          <label htmlFor="tree-name" className="text-sm font-medium">
            Tree name
          </label>
          <input
            id="tree-name"
            value={treeName}
            onChange={(event_) => setTreeName(event_.target.value)}
            className="w-full max-w-sm rounded-md border bg-background px-3 py-1.5 font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            This becomes the <Prop name="d" /> tag. Publishing again with the same name replaces the root, which is how a
            tree gets a stable address that can still change.
          </p>
        </div>

        <div className="space-y-2 pt-2">
          <p className="text-sm font-medium">Who can open it</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {VISIBILITY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setVisibility(option.value)}
                className={cn(
                  "rounded-lg border p-2.5 text-left transition-colors",
                  visibility === option.value ? "border-primary bg-primary/10" : "hover:bg-muted",
                )}
              >
                <p className="text-xs font-medium">{option.label}</p>
                <p className="mt-0.5 text-[0.7rem] text-muted-foreground">{option.detail}</p>
              </button>
            ))}
          </div>
        </div>

        {visibility === "owner-private" && (
          <SpecNote tone="warn">
            The key for this walkthrough is a throwaway generated in your browser. An owner-private root means nobody —
            including any other app you paste the link into — can open the tree without that key.
          </SpecNote>
        )}

        {linkKey && (
          <FieldGroup>
            <Field
              label="link key"
              value={toHex(linkKey)}
              tone="key"
              hint="Not in the event. It travels as ?k=… in the reference, so whoever has the link has access."
            />
          </FieldGroup>
        )}

        <JsonBlock value={{ ...event, sig: `${event.sig.slice(0, 16)}…` }} caption={`signed kind ${ROOT_KIND} event`} />
      </Step>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void publish()}
          disabled={publishing}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {publishing ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          Publish to {(relays.length ? relays : DEFAULT_RELAYS).length} relays
        </button>
      </div>

      {outcomes && (
        <div className="overflow-hidden rounded-lg border">
          {outcomes.map((outcome) => (
            <div key={outcome.relay} className="flex items-center gap-2 border-b px-3 py-2 text-xs last:border-b-0">
              {outcome.ok ? <Check className="size-3.5 text-emerald-500" /> : <X className="size-3.5 text-red-500" />}
              <span className="font-mono">{outcome.relay}</span>
              {outcome.message && <span className="text-muted-foreground">{outcome.message}</span>}
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={!published}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {published ? "See your links" : "Publish to at least one relay to continue"}
      </button>
    </Slide>
  );
}
